<template>
  <div class="w-full">
    <div class="mb-8 flex justify-between items-center">
      <BasePageHeader class="!mb-0">explore</BasePageHeader>

      <button
        class="btn btn-xs flex items-center justify-start space-x-1"
        @click="shuffle()"
      >
        <IconDiceOutline />
        <div>Shuffle</div>
      </button>
    </div>

    <BaseList
      title="random mews"
      class="mb-8"
      :items="randomMews"
      :is-loading="isLoadingRandomMewHashes || isFetchingRandomMews"
    >
      <template #default="{ item }">
        <BaseMewListItem
          :feed-mew="item"
          @mew-deleted="patchRandomMew"
          @mew-licked="patchRandomMew"
          @mew-unlicked="patchRandomMew"
          @mew-pinned="patchRandomMew"
          @mew-unpinned="patchRandomMew"
          @mewmew-created="patchRandomMew"
          @reply-created="patchRandomMew"
          @quote-created="patchRandomMew"
        />
      </template>
      <template #loading>
        <BaseListSkeleton :count="4">
          <BaseMewListItemSkeleton />
        </BaseListSkeleton>
      </template>
    </BaseList>

    <RandomMewWithTagList v-if="tag1 !== undefined" :tag="tag1" />
    <RandomMewWithTagList v-if="tag2 !== undefined" :tag="tag2" />
    <RandomMewWithTagList v-if="tag3 !== undefined" :tag="tag3" />
  </div>
</template>

<script setup lang="ts">
import { ComputedRef, computed, inject, onMounted, toRaw, watch } from "vue";
import { useCellsReady } from "@/composables/useCellsReady";
import { AppClient } from "@holochain/client";
import BaseList from "@/components/BaseList.vue";
import { FeedMew } from "@/types/types";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { usePatchFeedMew } from "@/composables/usePatchFeedMew";
import IconDiceOutline from "~icons/ion/dice-outline";
import { ActionHash } from "@holochain/client";
import BaseListSkeleton from "@/components/BaseListSkeleton.vue";
import BaseMewListItemSkeleton from "@/components/BaseMewListItemSkeleton.vue";
import { wrapInput } from "@/utils/zomeCall";

const client = (inject("client") as ComputedRef<AppClient>).value;
const cellsReady = useCellsReady();
const { patchFeedMew } = usePatchFeedMew();

const fetchRandomMewHashes = (): Promise<ActionHash[]> =>
  client.callZome({
    role_name: "mewsfeed",
    zome_name: "mews",
    fn_name: "get_random_mew_hashes",
    payload: wrapInput(3),
  });

const fetchMewsWithContext = async (hashes: ActionHash[]): Promise<FeedMew[]> =>
  client.callZome({
    role_name: "mewsfeed",
    zome_name: "mews",
    fn_name: "get_batch_mews_with_context",
    payload: wrapInput(hashes),
  });

const {
  data: randomMewHashes,
  error: errorRandomMewHashes,
  isLoading: isLoadingRandomMewHashes,
  refetch: refetchRandomMewHashes,
} = useQuery({
  queryKey: ["mews", "get_random_mew_hashes"],
  queryFn: () => fetchRandomMewHashes(),
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  enabled: cellsReady,
});
watch(errorRandomMewHashes, console.error);

const hasRandomMewHashes = computed(
  () => randomMewHashes.value !== undefined && randomMewHashes.value.length > 0
);

const randomMewsQueryKey = ["mews", "get_random_mew_hashes", "get_batch_mews_with_context"];

const patchRandomMew = (updatedMew: FeedMew) => {
  patchFeedMew(randomMewsQueryKey, updatedMew);
};

const {
  data: randomMews,
  error: errorRandomMews,
  refetch: refetchRandomMews,
  isFetching: isFetchingRandomMews,
} = useQuery({
  queryKey: randomMewsQueryKey,
  enabled: computed(() => cellsReady.value && hasRandomMewHashes.value),
  queryFn: () =>
    fetchMewsWithContext(toRaw(randomMewHashes.value as ActionHash[])),
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
});
watch(errorRandomMews, console.error);

const fetchRandomTags = (): Promise<string[]> => {
  return client.callZome({
    role_name: "mewsfeed",
    zome_name: "mews",
    fn_name: "get_random_tags",
    payload: wrapInput(4),
  });
};

const {
  data: randomTags,
  error: errorRandomTags,
  refetch: refetchRandomTags,
} = useQuery({
  queryKey: ["mews", "get_random_tags"],
  queryFn: fetchRandomTags,
  initialData: [],
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  enabled: cellsReady,
});
watch(errorRandomTags, console.error);

onMounted(async () => {
  if (randomMews.value === undefined || randomMews.value.length === 0) {
    await refetchMews();
  }
  if (randomTags.value === undefined || randomTags.value.length === 0) {
    refetchRandomTags();
  }
});

const tag1 = computed(() =>
  randomTags.value ? randomTags.value[0] : undefined
);
const tag2 = computed(() =>
  randomTags.value ? randomTags.value[1] : undefined
);
const tag3 = computed(() =>
  randomTags.value ? randomTags.value[2] : undefined
);

const shuffle = async () => {
  await refetchMews();
  await refetchRandomTags();
};

const refetchMews = async () => {
  await refetchRandomMewHashes();
  if (hasRandomMewHashes.value) {
    refetchRandomMews();
  }
};
</script>
